import { useQueryClient } from "@tanstack/react-query";
import { FC, FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useGetLanguages } from "~/api/languages/languages";
import {
  GetOrganizationSettings200LlmResidency,
  Language,
  OrganizationRole,
} from "~/api/model";
import {
  getGetOrganizationQueryKey,
  getGetOrganizationSettingsQueryKey,
  useGetOrganization,
  useGetOrganizationSettings,
  useUpdateOrganization,
  useUpdateOrganizationSettings,
} from "~/api/organizations/organizations";
import { DisplayFor } from "~/components/permissions/DisplayFor";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Switch } from "~/components/ui/switch";
import { UpdateButton, UpdateButtonRef } from "~/components/UpdateButton";
import {
  PipelineMode,
  PipelineModeSelector,
} from "~/features/cases/components/PipelineModeSelector";
import { useOrganizationParams } from "~/helpers";
import { useTrackPageVisit } from "~/hooks/useTrackPageVisit";
import type { OrganizationSettingsWithBates } from "~/types/bates";
import { trackEvent } from "~/utils/analytics";

export const SettingsOrganization: FC = () => {
  useTrackPageVisit("app/settings_organization/visit");

  const intl = useIntl();
  const { organizationId } = useOrganizationParams();
  const { data: organization } = useGetOrganization(organizationId);
  const { data: organizationSettings, isLoading: isLoadingSettings } =
    useGetOrganizationSettings(organizationId);
  const { data: languages } = useGetLanguages();
  const { mutate, isPending } = useUpdateOrganization();
  const { mutate: updateSettings, isPending: isUpdatingSettings } =
    useUpdateOrganizationSettings();
  const updateButtonRef = useRef<UpdateButtonRef>(null);
  const queryClient = useQueryClient();

  const [selectedPipelineMode, setSelectedPipelineMode] =
    useState<PipelineMode>("USE_TOC");

  const [batesNumberingEnabled, setBatesNumberingEnabled] = useState(false);

  const [selectedLlmResidency, setSelectedLlmResidency] =
    useState<GetOrganizationSettings200LlmResidency>(
      GetOrganizationSettings200LlmResidency.GLOBAL
    );

  const [currentLanguage, setCurrentLanguage] = useState<
    Language | undefined
  >();

  const resolvedLanguage = useMemo(() => {
    const orgLanguage = organization?.language;
    if (!orgLanguage || !languages) return undefined;
    return Object.keys(languages).find(
      (key) => key.toLowerCase() === orgLanguage.toLowerCase()
    ) as Language | undefined;
  }, [organization?.language, languages]);

  useEffect(() => {
    if (resolvedLanguage) {
      setCurrentLanguage(resolvedLanguage);
    }
  }, [resolvedLanguage]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!currentLanguage) return;

    trackEvent({
      eventName: "app/settings_organization/language.select/change",
      properties: { language: currentLanguage },
    });

    trackEvent({
      eventName: "app/settings_organization/pipeline_mode.save",
      properties: { mode: selectedPipelineMode },
    });

    trackEvent({
      eventName: "app/settings_organization/llm_residency.save",
      properties: { residency: selectedLlmResidency },
    });

    mutate(
      { organizationId, data: { language: currentLanguage } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetOrganizationQueryKey(organizationId),
          });
        },
      }
    );

    updateSettings(
      {
        organizationId,
        data: {
          pipelineMode: selectedPipelineMode,
          llmResidency: selectedLlmResidency,
          batesNumberingEnabled,
        } as Parameters<typeof updateSettings>[0]["data"],
      },
      {
        onSuccess: (updatedSettings) => {
          updateButtonRef.current?.onSuccess();
          // Immediately update the cache with the new data
          queryClient.setQueryData(
            getGetOrganizationSettingsQueryKey(organizationId),
            updatedSettings
          );
        },
      }
    );
  };

  const handlePipelineModeChange = (mode: PipelineMode) => {
    setSelectedPipelineMode(mode);
    trackEvent({
      eventName: "app/settings_organization/pipeline_mode.select",
      properties: { mode },
    });
  };

  useEffect(() => {
    if (organizationSettings?.pipelineMode) {
      setSelectedPipelineMode(organizationSettings.pipelineMode);
    }
    if (organizationSettings?.llmResidency) {
      setSelectedLlmResidency(organizationSettings.llmResidency);
    }
    const settingsWithBates =
      organizationSettings as OrganizationSettingsWithBates;
    if (settingsWithBates?.batesNumberingEnabled !== undefined) {
      setBatesNumberingEnabled(settingsWithBates.batesNumberingEnabled);
    }
  }, [
    organizationSettings?.pipelineMode,
    organizationSettings?.llmResidency,
    (organizationSettings as OrganizationSettingsWithBates)
      ?.batesNumberingEnabled,
  ]);

  const handleLanguageChange = (value: string) => {
    setCurrentLanguage(value as Language);
  };

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col gap-6 mt-2 w-full max-w-[766px]"
    >
      <h3 className="text-2xl font-semibold">
        <FormattedMessage
          id="settings.organization"
          defaultMessage="Organization"
        />
      </h3>

      {/* Output Language Section */}
      <div className="flex flex-col gap-2">
        <Label htmlFor="organizationLanguage">
          <FormattedMessage
            id="settings.organization.outputLanguage"
            defaultMessage="Output Language"
          />
        </Label>
        <Select value={currentLanguage} onValueChange={handleLanguageChange}>
          <SelectTrigger
            id="organizationLanguage"
            onClick={() => {
              trackEvent({
                eventName: "app/settings_organization/language.select/click",
              });
            }}
          >
            <SelectValue
              placeholder={intl.formatMessage({
                id: "settings.organization.selectLanguage",
                defaultMessage: "Select a language",
              })}
            />
          </SelectTrigger>
          <SelectContent>
            {languages &&
              Object.entries(languages).map(([key, value]) => (
                <SelectItem key={key} value={key}>
                  {value.label}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">
          <FormattedMessage
            id="settings.organization.outputLanguageHint"
            defaultMessage="This language will be used for AI-generated content such as summaries, classifications, and chat responses."
          />
        </span>
      </div>

      {/* Pipeline Mode Section - Only visible to OWNER and SUPERADMIN */}
      <DisplayFor roles={[OrganizationRole.OWNER, OrganizationRole.SUPERADMIN]}>
        <div className="flex flex-col gap-4 border-t pt-6">
          <div>
            <Label>
              <FormattedMessage
                id="settings.organization.pipelineMode.title"
                defaultMessage="Document Processing Mode"
              />
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              <FormattedMessage
                id="settings.organization.pipelineMode.description"
                defaultMessage="Choose how Sky AI handles document structure and organization for new uploads."
              />
            </p>
          </div>

          <PipelineModeSelector
            selectedMode={selectedPipelineMode}
            onModeChange={handlePipelineModeChange}
            variant="detailed"
          />
        </div>
      </DisplayFor>

      {/* LLM Data Residency Section - Only visible to OWNER and SUPERADMIN */}
      <DisplayFor roles={[OrganizationRole.OWNER, OrganizationRole.SUPERADMIN]}>
        <div className="flex flex-col gap-2 border-t pt-6">
          <Label htmlFor="llmResidency">
            <FormattedMessage
              id="settings.organization.llmResidency.title"
              defaultMessage="LLM Data Residency"
            />
          </Label>
          <p className="text-sm text-muted-foreground">
            <FormattedMessage
              id="settings.organization.llmResidency.description"
              defaultMessage="Choose the region where AI processing of your organization's data takes place."
            />
          </p>
          <Select
            value={selectedLlmResidency}
            onValueChange={(value) => {
              const residency = value as GetOrganizationSettings200LlmResidency;
              setSelectedLlmResidency(residency);
              trackEvent({
                eventName: "app/settings_organization/llm_residency.select",
                properties: { residency },
              });
            }}
          >
            <SelectTrigger id="llmResidency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={GetOrganizationSettings200LlmResidency.GLOBAL}>
                {intl.formatMessage({
                  id: "settings.organization.llmResidency.global",
                  defaultMessage: "Global",
                })}
              </SelectItem>
              <SelectItem
                value={GetOrganizationSettings200LlmResidency.MONTREAL_CANADA}
              >
                {intl.formatMessage({
                  id: "settings.organization.llmResidency.montrealCanada",
                  defaultMessage: "Montreal, Canada",
                })}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </DisplayFor>

      {/* Bates Numbering Section - Only visible to OWNER and SUPERADMIN */}
      <DisplayFor roles={[OrganizationRole.OWNER, OrganizationRole.SUPERADMIN]}>
        <div className="flex flex-col gap-4 border-t pt-6">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="batesNumbering">
                <FormattedMessage
                  id="settings.organization.batesNumbering.title"
                  defaultMessage="Enable Bates Numbering"
                />
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                <FormattedMessage
                  id="settings.organization.batesNumbering.description"
                  defaultMessage="Automatically stamp sequential Bates numbers on every page during document processing."
                />
              </p>
            </div>
            <Switch
              id="batesNumbering"
              checked={batesNumberingEnabled}
              onCheckedChange={(checked) => {
                setBatesNumberingEnabled(checked);
                trackEvent({
                  eventName: "app/settings_organization/bates_numbering.toggle",
                  properties: { enabled: checked },
                });
              }}
            />
          </div>
        </div>
      </DisplayFor>

      <UpdateButton
        isPending={isPending || isUpdatingSettings}
        disabled={isLoadingSettings}
        ref={updateButtonRef}
      />
    </form>
  );
};
